import * as React from 'react';
import { IEmployeeErrorData, ILabEmployee } from 'typings/employeeErrorData';
import { teamErrorData } from 'utils/MobxDataManager/singletons';
import { validateEmail } from 'utils/emailValidatorUtils';
import EditModalFooter from 'nomst/Components/Configuration/editModalFooter';
import EditModalHeader from 'nomst/Components/Configuration/editModalHeader';
import EditModalBody from 'nomst/Components/Configuration/editModalBody';
import './employeeEditModal.scss';
import { IEmployeeIdMap } from 'typings/employee';
import { ICurrentEmails } from 'typings/diagnostics';

export interface EmployeeEditViewProps {
    employeeIds: IEmployeeIdMap;
    employeeErrorData: IEmployeeErrorData;
    onSubmitSuccessCallBackFn: () => void;
    onCancelFn: () => void;
    isEmployeeLastOneWithoutManagerId: () => boolean;
    allEmails: ICurrentEmails;
}

interface EditEmployeeState {
    isSending: boolean;
    labEmployee: Nullable<ILabEmployee>;
    isSaveError: boolean;
    email: NullOrUndefined<string>;
    errorDetails: { [id: string]: string };
}

export default class EmployeeEditModal extends React.Component<EmployeeEditViewProps, EditEmployeeState> {
    constructor(props: EmployeeEditViewProps) {
        super(props);
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleSetWorkEmail = this.handleSetWorkEmail.bind(this);
        this.handleErrorBannerClose = this.handleErrorBannerClose.bind(this);
        this.isFormValid = this.isFormValid.bind(this);
        this.onUpdateCallBack = this.onUpdateCallBack.bind(this);
        this.isEmptyManagerIdValid = this.isEmptyManagerIdValid.bind(this);

        this.state = {
            isSending: false,
            isSaveError: false,
            labEmployee: this.props.employeeErrorData.labEmployee,
            email: this.props.employeeErrorData.teamEmployee.workEmail,
            errorDetails: {...this.props.employeeErrorData.errorDetails}};
    }

    render(): React.ReactNode {
        return (
            <div className="pxl-modal">
                <div className="nomst-employee-edit-modal">
                    <button
                        className="nomst-employee-edit-modal__close"
                        onClick={this.handleCancelClick}
                    >
                        <i className="icon-cancel" />
                    </button>
                    <EditModalHeader
                        labEmployee={this.state.labEmployee}
                        errorDetails={this.state.errorDetails}
                    />
                    <EditModalBody
                        employeeIds={this.props.employeeIds}
                        email ={this.state.email}
                        labEmployee={this.state.labEmployee}
                        errorDetails={this.state.errorDetails}
                        isSending={this.state.isSending}
                        onUpdateCallBack={this.onUpdateCallBack}
                        handleSetWorkEmail={this.handleSetWorkEmail}
                        isEmptyManagerIdValid={this.isEmptyManagerIdValid}
                    />
                    <EditModalFooter
                        isSending={this.state.isSending}
                        isSaveError={this.state.isSaveError}
                        errorDetails={this.state.errorDetails}
                        isFormValid={this.isFormValid}
                        handleCancelClick={this.handleCancelClick}
                        handleSaveClick={this.handleSaveClick}
                        handleErrorBannerClose={this.handleErrorBannerClose}
                    />
                </div>
            </div>
        );
    }

    private handleSetWorkEmail(e: React.ChangeEvent<HTMLInputElement>): void {
        const { labEmployee } = { ...this.state };
        if (e?.target?.value != null && labEmployee) {
            let { errorDetails } = { ...this.state };
            labEmployee.email = e.target.value;
            if(!labEmployee?.email.length ||
                this.validateEmailAndCheckForDuplicates(labEmployee.email, labEmployee.employeeNumber ?? '')) {
                delete errorDetails['workEmail'];
            } else {
                errorDetails = Object.assign( errorDetails, {'workEmail':'missing'} );
            }
            this.setState({ labEmployee, errorDetails, email: e.target.value });
        }
    }

    private validateEmailAndCheckForDuplicates(email: string, employeeNumber: string): boolean {
        if (!validateEmail(email)) {
            return false;
        }
        const { allEmails } = this.props;
        const emailArray = allEmails[email];
        if (emailArray) {
            return emailArray.filter(employee => employee.employeeSourceId === employeeNumber).length > 0;
        }
        return true;
    }

    private onUpdateCallBack(labEmployee: ILabEmployee, errorDetails: { [id: string]: string }){
        this.setState({ labEmployee, errorDetails });
    }

    private handleErrorBannerClose(): void {
        this.setState({ isSaveError: false });
    }

    private handleCancelClick(): void {
        this.props.onCancelFn();
    }

    private handleCloseClick(): void {
        this.props.onSubmitSuccessCallBackFn();
    }

    private async handleSaveClick(): Promise<void> {
        this.setState({ isSending: true });

        try {
            const { labEmployee } = { ...this.state };
            if (labEmployee && labEmployee.email == null) {
                labEmployee.email = this.state.email;
                this.setState({ labEmployee});
            }
            if (this.isFormValid()) {
                const result = await teamErrorData.updateLabEmployee(labEmployee);
                if (result?.success == null || result?.success) {
                    this.handleCloseClick();
                }
            } else {
                this.setState({ isSaveError: true, isSending: false});
            }
        } catch (e) {
            this.setState({ isSaveError: true, isSending: false});
        }
    }

    private isFormValid(): boolean {
        const { labEmployee } = { ...this.state };
        let isEmailValid = true;
        if (labEmployee?.email?.length) {
            isEmailValid = validateEmail(labEmployee.email);
        }
        return isEmailValid && this.validateNonEmailEmployeeFields(labEmployee) && this.isEmptyManagerIdValid(labEmployee);
    }

    private isEmptyManagerIdValid(labEmployee: Nullable<ILabEmployee>): boolean {
        const isEmployeeCeo = this.props.isEmployeeLastOneWithoutManagerId();
        const hasManagerIdUpdated = !!(Object.keys(this.state.errorDetails).filter(key => key === 'managerNumber').length === 0);
        return isEmployeeCeo || (hasManagerIdUpdated && !!labEmployee?.managerNumber?.trim()) ;
    }

    private validateNonEmailEmployeeFields(labEmployee: Nullable<ILabEmployee>): boolean {
        return !!labEmployee?.firstName?.trim()
            && !!labEmployee?.lastName?.trim()
            && !!labEmployee?.employeeNumber?.trim()
            && !!labEmployee?.orgJobId
            && !!labEmployee?.orgJobTitle?.trim()
            && !!labEmployee?.annualOrHourly?.trim()
            && !!labEmployee?.standardWorkHours;
    }
}